import { useOrganization } from "@/lib/organization/organization-context"
import { useConnectedBodyshop, useTeam } from "@/lib/api/use-teams"
import { feesNotReviewed } from "@/lib/fees/defaults"

// Prerequisites that must be satisfied before a user can start an estimate.
// Drives the route-level <EstimatePrerequisiteGate>. Composes the same query
// primitives as useSetupTasks so the warning + gate can never disagree.
//
// Both blockers are hard:
//   - bodyshop: a dealership has nowhere to send the estimate without one —
//     proceeding produces an undeliverable record.
//   - fees:     a body shop's seeded placeholder rates aren't trustworthy
//     for real pricing; sending wrong numbers to customers is exactly what
//     the gate exists to prevent.

export type EstimateBlocker = "fees" | "bodyshop"

export interface EstimatePrerequisitesResult {
  isLoading: boolean
  ready: boolean
  blocker: EstimateBlocker | null
}

export function useEstimatePrerequisites(): EstimatePrerequisitesResult {
  const {
    activeTeamKind,
    activeTeamId,
    activeDealershipId,
    isLoading: orgLoading,
  } = useOrganization()

  const teamId = activeTeamId ? Number(activeTeamId) : null

  // Hooks must always be called; they're only `enabled` when the team kind
  // matches, so the conditional disablement happens inside TanStack Query.
  const bodyshopQuery = useConnectedBodyshop(
    activeTeamKind === "dealership" ? activeDealershipId : null,
  )
  const teamQuery = useTeam(activeTeamKind === "bodyshop" ? teamId : null)

  // While the org context is still resolving, we can't trust activeTeamKind.
  // Returning ready:true here would race the user past the gate during the
  // first render after sign-up. Stay in loading until org context is settled.
  if (orgLoading) {
    return { isLoading: true, ready: false, blocker: null }
  }

  // Org resolved but no active team — the user shouldn't be reaching
  // estimate routes at all in this state (ProtectedRoute / IndexRedirect
  // catch this earlier). Pass through so downstream guards can handle it.
  if (!activeTeamKind || !teamId) {
    return { isLoading: false, ready: true, blocker: null }
  }

  if (activeTeamKind === "dealership") {
    if (bodyshopQuery.isLoading) {
      return { isLoading: true, ready: false, blocker: null }
    }
    if (!bodyshopQuery.data) {
      return { isLoading: false, ready: false, blocker: "bodyshop" }
    }
  }

  if (activeTeamKind === "bodyshop") {
    if (teamQuery.isLoading) {
      return { isLoading: true, ready: false, blocker: null }
    }
    if (teamQuery.data && feesNotReviewed(teamQuery.data.metadata)) {
      return { isLoading: false, ready: false, blocker: "fees" }
    }
  }

  return { isLoading: false, ready: true, blocker: null }
}
